import { toast } from "@hortis/ui/sonner";
import {
  useArchiveCollectionSiteLocationMutation,
  useGetCollectionSiteLocationQuery,
} from "generated/graphql";
import { useCallback } from "react";
import { DeleteModal } from "src/components/dialog-mui/delete-dialog/delete-dialog";

interface DeleteLocationDialogProps {
  deleteLocationId: string | null;
  closeDeleteModal: () => void;
  refetchLocations: () => void;
}

export const DeleteLocationDialog = ({
  deleteLocationId,
  closeDeleteModal,
  refetchLocations,
}: DeleteLocationDialogProps) => {
  const [{ data, fetching }] = useGetCollectionSiteLocationQuery({
    variables: { id: deleteLocationId ?? "" },
    pause: deleteLocationId == null,
  });
  const [, archiveLocation] = useArchiveCollectionSiteLocationMutation();

  const location = data?.collectionSiteLocation;

  const handleDeleteLocation = useCallback(async () => {
    if (deleteLocationId != null) {
      try {
        const res = await archiveLocation({
          id: deleteLocationId,
        });
        if (res.data?.archiveCollectionSiteLocation.success === true) {
          toast.success("Location deleted");
          refetchLocations();
        } else {
          throw new Error("Failed to delete location");
        }
      } catch {
        toast.error("Failed to delete location");
      }
      closeDeleteModal();
    }
  }, [archiveLocation, deleteLocationId, refetchLocations, closeDeleteModal]);

  const hasAssociatedPlantMaterial =
    location?.totalPlantMaterial != null && location.totalPlantMaterial > 0;

  return (
    <DeleteModal
      title="Delete location"
      body={
        hasAssociatedPlantMaterial && !fetching
          ? `To delete this location, please remove the ${
              location.totalPlantMaterial ?? ""
            } associated non-absent plant material.`
          : `Are you sure you want to delete this location?`
      }
      onSubmit={handleDeleteLocation}
      open={deleteLocationId != null}
      onClose={closeDeleteModal}
      submitButtonProps={{
        disabled: hasAssociatedPlantMaterial || location == null,
        loading: location == null || fetching,
      }}
      testId="delete-location-dialog"
    />
  );
};
